import 'vscode/localExtensionHost'

import React, { lazy, Suspense, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes as ReactRouterRoutes } from 'react-router-dom'
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import ReactGA from 'react-ga4'
import Routes from './constants/routes'
import 'diff2html/bundles/css/diff2html.min.css';

import NotFoundPage from './pages/not-found'
import QuadrantLoader from './components/loaders/quadrant-loader/QuadrantLoader'
import { actions as authActions } from './store/reducers/auth.reducer'
import { isCurrentRoute } from './utils/link.helper'
import AuthorizedRoute from './components/authorization/AuthorizedRoute'
import UnauthorizedRoute from './components/authorization/UnauthorizedRoute'
import { UserRoles } from './constants'
import { initAppInsights, reactPlugin } from './configs/app-insights.config'
import { Result } from 'antd'
import Clarity from '@microsoft/clarity'
import useNotificationChannel from './hooks/useNotificationChannel'
import { useAuthSync } from './hooks/useAuthSync'
import { AssignmentSections } from './constants/assignment-sections'
import SettingsSections from './constants/settings-sections'
import SecuritySection from './pages/settings/sections/security/SecuritySection'
import UsersSection from './pages/settings/sections/users/UsersSection'
import OrganizationsSection from './pages/settings/sections/organizations/OrganizationsSection'
import LmsSection from './pages/settings/sections/lms/LmsSection'
import HomeSection from './pages/assignment/sections/home/HomeSection'
import DiscoveryDescriptionsReviewSection from './pages/assignment/sections/discovery-descriptions-review/DiscoveryDescriptionsReviewSection'
import FeedbackFromStudentsSection from './pages/assignment/sections/reports/feedback-from-students-section/FeedbackFromStudentsSection'
import AnalyticsReportSection from './pages/assignment/sections/reports/analytics-section/AnalyticsReportSection'
import PatternReportSection from './pages/assignment/sections/reports/PatternReportSection'
import HeatmapReportSection from './pages/assignment/sections/reports/HeatmapReportSection'
import SummaryReportSection from './pages/assignment/sections/reports/SummaryReportSection'
import UnitTestsSection from './pages/assignment/sections/unit-tests/UnitTestsSection'
import QuestionsSection from './pages/assignment/sections/questions/QuestionsSection'
import PatternsSection from './pages/assignment/sections/patterns/PatternsSection'
import RubricSection from './pages/assignment/sections/rubric/RubricSection'
import ComponentsSection from './pages/assignment/sections/components/ComponentsSection'
import ImportRubricSection from './pages/assignment/components/section-menu/import/ImportRubricSection'
import RubricRefinementSection from './pages/assignment/components/section-menu/rubric-refinement/RubricRefinementSection'
import { UserTypes } from './constants/UserRoles'

const InstantFeedback = lazy(() => import('./pages/InstantFeedback/InstantFeedback'))
const AssignmentsPage = lazy(() => import('./pages/resources/assignments/AssignmentsPage'))
const ProblemSetsPage = lazy(() => import('./pages/resources/problem-sets/ProblemSetsPage'))
const AssignmentPage = lazy(() => import('./pages/assignment/AssignmentPage'))
const LoginPage = lazy(() => import('./pages/auth/login/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/forgot-password/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/auth/reset-password/ResetPasswordPage'))
const AccountSetupPage = lazy(() => import('./pages/auth/account-setup/AccountSetupPage'))
const TermsOfUsePage = lazy(() => import('./pages/legal/TermsOfUsePage'))
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'))
const AccessibilityPage = lazy(() => import('./pages/legal/AccessibilityPage'))
const MaintenancePage = lazy(() => import('./pages/maintenance/MaintenancePage'))
const SNHUActivationPage = lazy(() => import('./pages/snhu-activation/SNHUActivationPage'))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const CatalogPage = lazy(() => import('./pages/resources/catalog/CatalogPage'))
const CatalogItemPage = lazy(() => import('./pages/catalog-item/CatalogItemPage'))
const CartPage = lazy(() => import('./pages/cart/CartPage'))
const ConceptualLabelsPage = lazy(() => import('./pages/resources/conceptual-labels/ConceptualLabelsPage'))
const CRITICAL_EMAILS_TO_RECORD = ['jamalb2@illinois.edu', 'ksaul4@illinois.edu']

const App = () => {
    useNotificationChannel()
    useAuthSync()
    const dispatch = useDispatch()
    const isServerOnMaintenance = useSelector(state => state.global.isServerOnMaintenance)

    useEffect(() => {
        ;(async () => {
            let config
            let user
            await Promise.all([
                dispatch(authActions.fetchAppConfig()).then(response => (config = response)),
                dispatch(authActions.fetchCurrentUser()).then(response => (user = response)),
            ])
            if (!config) return
            if (config.instrumentationKey) initAppInsights(config.instrumentationKey)
            if (config.googleAnalyticsMeasurementId) ReactGA.initialize(config.googleAnalyticsMeasurementId)
            if (config.clarityProjectId) {
                if (user?.record === true || (user?.id && isCurrentRoute(Routes.INSTANT_FEEDBACK))) {
                    Clarity.init(config.clarityProjectId)
                    Clarity.setTag('userEmail', user.email)
                    Clarity.identify(user.id, undefined, undefined, user.email)
                    if (CRITICAL_EMAILS_TO_RECORD.includes(user.email)) Clarity.upgrade(user.email)
                    console.log(`Clarity initialized with ${user.email}`)
                } else if (isCurrentRoute(Routes.INSTANT_FEEDBACK)) {
                    Clarity.init(config.clarityProjectId)
                    console.log('Clarity initialized')
                }
            }
        })()
    }, [dispatch])

    return (
        <AppInsightsErrorBoundary
            onError={() => {
                if (Clarity) Clarity.setTag('crash', 'true')
                return (
                    <div className='d-flex-center vh-100'>
                        <Result status='500' title='Sorry, something went wrong.' subTitle='Try to reload the page.' />
                    </div>
                )
            }}
            appInsights={reactPlugin}
        >
            <BrowserRouter basename={'/'}>
                <Suspense fallback={<QuadrantLoader />}>
                    {isServerOnMaintenance ? (
                        <MaintenancePage />
                    ) : (
                        <ReactRouterRoutes>
                            <Route
                                path={Routes.LOGIN}
                                element={
                                    <UnauthorizedRoute>
                                        <LoginPage />
                                    </UnauthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.FORGOT_PASSWORD}
                                element={
                                    <UnauthorizedRoute>
                                        <ForgotPasswordPage />
                                    </UnauthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.RESET_PASSWORD}
                                element={
                                    <UnauthorizedRoute>
                                        <ResetPasswordPage />
                                    </UnauthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.ACCOUNT_SETUP}
                                element={
                                    <UnauthorizedRoute>
                                        <AccountSetupPage />
                                    </UnauthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.HOME}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <AssignmentsPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.ASSIGNMENTS}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <AssignmentsPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.PROBLEM_SETS}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <ProblemSetsPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.CONCEPTUAL_LABELS}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <ConceptualLabelsPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.ASSIGNMENT}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <AssignmentPage />
                                    </AuthorizedRoute>
                                }
                            >
                                <Route index element={<Navigate to={AssignmentSections.HOME} replace />} />
                                <Route path={AssignmentSections.HOME} element={<HomeSection />} />
                                <Route path={AssignmentSections.RUBRIC} element={<RubricSection />} />
                                <Route path={AssignmentSections.PATTERNS} element={<PatternsSection />} />
                                <Route path={AssignmentSections.COMPONENTS} element={<ComponentsSection />} />
                                <Route path={AssignmentSections.QUESTIONS} element={<QuestionsSection />} />
                                <Route path={AssignmentSections.UNIT_TESTS} element={<UnitTestsSection />} />
                                <Route
                                    path={AssignmentSections.REPORTS.SUMMARY}
                                    element={
                                        <AuthorizedRoute roles={[UserRoles.REPORT_VIEWER]}>
                                            <SummaryReportSection />
                                        </AuthorizedRoute>
                                    }
                                />
                                <Route
                                    path={AssignmentSections.REPORTS.HEATMAP}
                                    element={
                                        <AuthorizedRoute roles={[UserRoles.REPORT_VIEWER]}>
                                            <HeatmapReportSection />
                                        </AuthorizedRoute>
                                    }
                                />
                                <Route
                                    path={AssignmentSections.REPORTS.PATTERNS}
                                    element={
                                        <AuthorizedRoute roles={[UserRoles.REPORT_VIEWER]}>
                                            <PatternReportSection />
                                        </AuthorizedRoute>
                                    }
                                />
                                <Route
                                    path={AssignmentSections.REPORTS.ANALYTICS}
                                    element={
                                        <AuthorizedRoute roles={[UserRoles.REPORT_VIEWER]}>
                                            <AnalyticsReportSection />
                                        </AuthorizedRoute>
                                    }
                                />
                                <Route
                                    path={AssignmentSections.REPORTS.FEEDBACK_FROM_STUDENTS}
                                    element={<FeedbackFromStudentsSection />}
                                />
                                <Route
                                    path={AssignmentSections.DISCOVERY_DESCRIPTIONS_REVIEW}
                                    element={<DiscoveryDescriptionsReviewSection />}
                                />
                                <Route path={AssignmentSections.IMPORT_RUBRIC} element={<ImportRubricSection />} />
                                <Route path='*' element={<NotFoundPage />} />
                                <Route
                                    path={AssignmentSections.RUBRIC_REFINEMENT}
                                    element={<RubricRefinementSection />}
                                />
                            </Route>
                            <Route
                                path={Routes.SETTINGS}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <SettingsPage />
                                    </AuthorizedRoute>
                                }
                            >
                                <Route index element={<Navigate to={SettingsSections.SECURITY} replace />} />
                                <Route path={SettingsSections.SECURITY} element={<SecuritySection />} />
                                <Route path={SettingsSections.USERS} element={<UsersSection />} />
                                <Route
                                    path={SettingsSections.ORGANIZATIONS}
                                    element={
                                        <AuthorizedRoute roles={[UserRoles.ADMIN]}>
                                            <OrganizationsSection />
                                        </AuthorizedRoute>
                                    }
                                />
                                <Route
                                    path={SettingsSections.LMS}
                                    element={
                                        <AuthorizedRoute
                                            senseUserOnly
                                            roles={[UserRoles.LMS_CONFIGURATOR, UserRoles.ADMIN]}
                                            types={[UserTypes.USER, UserTypes.SYSTEM_ADMIN]}
                                        >
                                            <LmsSection />
                                        </AuthorizedRoute>
                                    }
                                />
                                <Route path='*' element={<NotFoundPage />} />
                            </Route>
                            <Route
                                path={Routes.CART}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <CartPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.CATALOG}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <CatalogPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route
                                path={Routes.CATALOG_ITEM}
                                element={
                                    <AuthorizedRoute roles={[UserRoles.REGULAR, UserRoles.ADMIN]}>
                                        <CatalogItemPage />
                                    </AuthorizedRoute>
                                }
                            />
                            <Route path={Routes.SNHU_ACTIVATION} element={<SNHUActivationPage />} />
                            <Route path={Routes.INSTANT_FEEDBACK} element={<InstantFeedback />} />
                            <Route path={Routes.TERMS_OF_USE} element={<TermsOfUsePage />} />
                            <Route path={Routes.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
                            <Route path={Routes.ACCESSIBILITY} element={<AccessibilityPage />} />
                            <Route path='*' element={<NotFoundPage />} />
                        </ReactRouterRoutes>
                    )}
                </Suspense>
            </BrowserRouter>
        </AppInsightsErrorBoundary>
    )
}

export default App
